import React from 'react';

function componentName(component) {
  return component?.display_name || component?.name || 'Unknown component';
}

function pluralize(count, singular, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

export default function ComponentDetailView({ selectedSystem, componentDetail, findingsHref, componentMetricValue }) {
  const component = componentDetail.component;
  const linkedFindingCount = componentDetail.linked_findings.length;
  const impactedWorkflowCount = componentDetail.impacted_workflows.length;

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">{componentDetail.mapping_summary.provenance_badge}</p>
            <h2>{componentName(component)}</h2>
            <p className="panel-copy">
              {component.component_type} · {component.status}
              {component.owner ? ` · ${component.owner}` : ''}
            </p>
            <p className="overview-note">
              This component currently localizes {linkedFindingCount} {pluralize(linkedFindingCount, 'finding')} across {impactedWorkflowCount}{' '}
              {pluralize(impactedWorkflowCount, 'workflow')} based on persisted runtime mapping evidence.
            </p>
          </div>
          <div className="form-actions">
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/components`}>
              Back to components
            </a>
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/architecture?component=${component.id}`}>
              View in architecture
            </a>
          </div>
        </div>

        <div className="findings-summary-grid">
          <div className="detail-card summary-card">
            <span className="summary-card-label">Linked findings</span>
            <strong>{linkedFindingCount}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Impacted workflows</span>
            <strong>{impactedWorkflowCount}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Runtime mapping</span>
            <strong>{componentDetail.mapping_summary.observed_run_count}</strong>
          </div>
          <div className="detail-card summary-card summary-card-muted">
            <span className="summary-card-label">Identity rules</span>
            <strong>{componentDetail.identity_rules.length}</strong>
          </div>
        </div>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Summary</p>
              <h3>Current component runtime</h3>
            </div>
          </div>
          <div className="overview-decision-grid">
            <section className="detail-card overview-card">
              <strong>Metadata</strong>
              <div className="chip-row">
                {component.repository ? <span className="chip">Repo: {component.repository}</span> : null}
                {component.service_name ? <span className="chip">Service: {component.service_name}</span> : null}
              </div>
              <p className="overview-note">
                {componentDetail.mapping_summary.observed_run_count} observed runs · {componentDetail.mapping_summary.active_identity_rule_count} active identity rules
              </p>
            </section>

            <section className="detail-card overview-card">
              <strong>Current health</strong>
              <p>
                {componentDetail.health_summary.status === 'no_recent_analysis'
                  ? 'No recent component-scoped analysis is available yet.'
                  : `Latest component analysis covered ${componentDetail.health_summary.run_count} runs.`}
              </p>
              <div className="chip-row">
                {componentDetail.health_summary.metrics.map((metric) => (
                  <span className="chip" key={`detail-${metric.metric_name}`}>
                    {metric.display_label}: {componentMetricValue(metric)}
                  </span>
                ))}
              </div>
            </section>

            <section className="detail-card overview-card">
              <strong>What this affects</strong>
              <div className="labeled-stat-list">
                <div className="labeled-stat-row">
                  <span>Linked findings</span>
                  <strong>{linkedFindingCount} active signals</strong>
                </div>
                <div className="labeled-stat-row">
                  <span>Workflow impact</span>
                  <strong>
                    {impactedWorkflowCount} {pluralize(impactedWorkflowCount, 'workflow')}
                  </strong>
                </div>
                <div className="labeled-stat-row">
                  <span>Runtime mapping</span>
                  <strong>
                    {componentDetail.mapping_summary.observed_run_count} runs · {componentDetail.mapping_summary.observed_step_count} steps
                  </strong>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Linked work</p>
              <h3>Linked findings</h3>
            </div>
            <a className="pill-link" href={findingsHref(selectedSystem.id, { componentId: component.id })}>
              Filter findings
            </a>
          </div>
          {componentDetail.linked_findings.length ? (
            <div className="stack compact-stack">
              {componentDetail.linked_findings.map((finding) => (
                <a className="overview-row-link" href={`#/systems/${selectedSystem.id}/findings/${finding.id}`} key={finding.id}>
                  <div>
                    <strong>{finding.title}</strong>
                    <p>{finding.summary}</p>
                    <p className="overview-note">
                      {finding.workflow_name || 'Workflow context'} · {finding.status} · Rank #{finding.priority_rank}
                    </p>
                  </div>
                  <span className="chip">{finding.severity}</span>
                </a>
              ))}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No linked findings right now</strong>
              <p>This component does not currently have active localized findings.</p>
            </div>
          )}
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Workflow context</p>
              <h3>Impacted workflows</h3>
            </div>
          </div>
          {componentDetail.impacted_workflows.length ? (
            <div className="stack compact-stack">
              {componentDetail.impacted_workflows.map((workflow) => (
                <a
                  className="overview-row-link"
                  href={findingsHref(selectedSystem.id, { workflowId: workflow.workflow_id, componentId: component.id })}
                  key={workflow.workflow_id}
                >
                  <div>
                    <strong>{workflow.name}</strong>
                    <p>{workflow.step_count} mapped steps · {workflow.active_linked_finding_count} active findings</p>
                  </div>
                  <span className="chip">{workflow.linked_finding_count} linked</span>
                </a>
              ))}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No workflow impact recorded yet</strong>
              <p>This component has not been linked to workflow activity in the current persisted records.</p>
            </div>
          )}
        </section>

        <section className="overview-band">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Mapping</p>
              <h3>Mapping provenance</h3>
            </div>
          </div>
          <div className="overview-focus-grid">
            <section className="detail-card overview-card">
              <strong>How this component is identified</strong>
              <div className="chip-row">
                <span className="chip">Badge: {componentDetail.mapping_summary.provenance_badge}</span>
                {componentDetail.mapping_summary.inference_source_kind ? (
                  <span className="chip">Inference: {componentDetail.mapping_summary.inference_source_kind}</span>
                ) : null}
                {componentDetail.mapping_summary.inference_confidence_band ? (
                  <span className="chip">Confidence: {componentDetail.mapping_summary.inference_confidence_band}</span>
                ) : null}
              </div>
              {componentDetail.identity_rules.length ? (
                <div className="stack compact-stack">
                  {componentDetail.identity_rules.map((rule) => (
                    <div className="detail-card" key={rule.id}>
                      <strong>{rule.match_kind}</strong>
                      <p>{rule.raw_value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="panel-copy">No manual identity rules are currently active for this component.</p>
              )}
            </section>

            <section className="detail-card overview-card">
              <strong>Architecture context</strong>
              {componentDetail.architecture_context.related_components.length ? (
                <div className="stack compact-stack">
                  {componentDetail.architecture_context.related_components.map((relatedComponent) => (
                    <a className="overview-row-link" href={`#/systems/${selectedSystem.id}/components/${relatedComponent.id}`} key={relatedComponent.id}>
                      <div>
                        <strong>{componentName(relatedComponent)}</strong>
                        <p>{relatedComponent.component_type}</p>
                      </div>
                      <span className="chip">Related</span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="panel-copy">No related runtime relationships are available for this component yet.</p>
              )}
            </section>
          </div>
        </section>
      </div>
    </section>
  );
}
